import { useEffect, useRef, useState } from "react";

export default function CharactersSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const cappyTraits = [
    "Playful — loves splashing, exploring, climbing, and making everyone laugh",
    "Curious — always wants to know what is around the next corner",
    "Mischievous — never naughty, but often causes little problems by rushing in",
    "Brave — willing to try new things, even when he feels nervous",
    "Kind-hearted — when he makes a mistake, he feels sorry and wants to put it right",
  ];

  const barryTraits = [
    "Calm — he does not panic easily and helps Cappy slow down",
    "Wise — he notices things Cappy misses",
    "Protective — he looks after Cappy and their friends",
    "Patient — even when Cappy creates chaos, Barry stays kind",
    "Loyal — he will always stand by Cappy, no matter what",
  ];

  return (
    <section
      id="characters"
      className="relative py-16 md:py-24 px-4 md:px-8 lg:px-12 overflow-hidden"
    >
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-secondary-500" />
        <div className="absolute inset-0 bg-secondary-500/60" />
      </div>
      <div
        ref={ref}
        className={`relative z-10 w-full transition-all duration-700 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
        }`}
      >
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-1.5 bg-white/15 text-white text-sm font-semibold rounded-full mb-4 backdrop-blur-sm">
            Riverside Friends
          </span>
          <h2 className="font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4">
            Meet the Capybaras
          </h2>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto">
            Two very different friends who always have each other&apos;s backs.
            Together, they make the perfect team.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-8 md:gap-12 max-w-5xl mx-auto">
          {/* Cappy Card */}
          <div className="flex-1 bg-white/90 backdrop-blur-md rounded-2xl p-8 md:p-10 border border-white/20 shadow-lg shadow-foreground-950/10 text-center hover:shadow-xl hover:shadow-foreground-950/10 transition-all duration-300 hover:-translate-y-1 group relative">
            <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto mb-6">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 pointer-events-none">
                <div className="bg-primary-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm animate-bounce">
                  Hi, I&apos;m Cappy!
                </div>
                <div className="w-2 h-2 bg-primary-500 rotate-45 mx-auto -mt-1" />
              </div>
              <div className="w-full h-full rounded-full overflow-hidden border-4 border-primary-300 group-hover:scale-105 transition-transform duration-300 relative">
                <img
                  alt="Cappy - the playful capybara"
                  className="w-full h-full object-cover"
                  src="../../../../images/4d09d9645736fdaa0f3b231c9c13b6f0.png"
                />
              </div>
            </div>
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full mb-3">
              The Playful One
            </span>
            <h3 className="font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
              Cappy
            </h3>
            <p className="text-foreground-600 text-base leading-relaxed mb-4">
              Small, cheeky, and full of energy. Cappy&apos;s big heart and
              playful spirit keep every adventure fun &mdash; even when things get
              a little messy.
            </p>
            <ul className="text-left text-sm text-foreground-500 space-y-2">
              {cappyTraits.map((trait, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-primary-500 mt-0.5 shrink-0" />
                  <span>{trait}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-primary-700">
              <i className="ri-emotion-happy-line" />
              <span className="font-medium">Cappy brings the fun</span>
            </div>
          </div>

          {/* Barry Card */}
          <div className="flex-1 bg-white/90 backdrop-blur-md rounded-2xl p-8 md:p-10 border border-white/20 shadow-lg shadow-foreground-950/10 text-center hover:shadow-xl hover:shadow-foreground-950/10 transition-all duration-300 hover:-translate-y-1 group relative">
            <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto mb-6">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 z-10 pointer-events-none">
                <div className="bg-accent-500 text-white text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-sm animate-bounce">
                  Hello, I&apos;m Barry!
                </div>
                <div className="w-2 h-2 bg-accent-500 rotate-45 mx-auto -mt-1" />
              </div>
              <div className="w-full h-full rounded-full overflow-hidden border-4 border-accent-300 group-hover:scale-105 transition-transform duration-300 relative">
                <img
                  alt="Barry - the wise capybara"
                  className="w-full h-full object-cover"
                  src="../../../../images/668a3251ef5788bdee604c64ee59ddeb.png"
                />
              </div>
            </div>
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-semibold rounded-full mb-3">
              The Wise One
            </span>
            <h3 className="font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
              Barry
            </h3>
            <p className="text-foreground-600 text-base leading-relaxed mb-4">
              Bigger, calmer, and very clever. Barry&apos;s steady mind and warm
              patience guide him and Cappy through every challenge on the
              riverside.
            </p>
            <ul className="text-left text-sm text-foreground-500 space-y-2">
              {barryTraits.map((trait, i) => (
                <li key={i} className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-fill text-accent-500 mt-0.5 shrink-0" />
                  <span>{trait}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-accent-700">
              <i className="ri-emotion-happy-line" />
              <span className="font-medium">Barry brings the wisdom</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}